import { Link } from "@inertiajs/react"
import { MarketingFooter } from "@/components/marketing-footer"
import { Button } from "@/components/ui/button"
import { signUpPath } from "@/routes"

export default function HowItWorks() {
  // Props available via usePage


  return (
    <>


      {/* Hero Section */}
      <div className="border-b bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-6">
            <nav className="text-muted-foreground flex items-center space-x-2 text-sm">
              <Link
                href="/"
                className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground font-medium">How It Works</span>
            </nav>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-foreground mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              How ThaiCopilot Works
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Watch how ThaiCopilot transforms your Thai learning experience
              through AI conversations, instant translations, and smart flashcards.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-white dark:bg-slate-950">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          {/* Video Section */}
          <section className="mb-16">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-xl dark:border-slate-800 dark:bg-slate-900">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/3enuWHXDxX0"
                  title="How ThaiCopilot Works - Full Walkthrough"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>
          </section>

          {/* Content Sections */}
          <div className="prose prose-slate dark:prose-invert prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-p:text-base prose-p:leading-relaxed prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-ul:text-base prose-ul:leading-relaxed max-w-none">

            {/* AI-Powered Thai Conversations */}
            <section>
              <h2>AI-Powered Thai Conversations</h2>
              <p>
                ThaiCopilot starts with conversations — the foundation of learning Thai from practical and personal context.
                Unlike traditional apps that focuses too much on rote memorization and gamification, ThaiCopilot helps you
                think in context. Type any English phrase you'd use in real life, such as ordering food,
                asking for directions, chatting with neighbors—and instantly get the Thai translation,
                audio pronunciation, romanization, and detailed explanation.
              </p>
              <p>
                Each conversation becomes a personalized learning session. The AI understands context
                and provides culturally appropriate translations, not just word-for-word conversions.
                This approach mirrors how you actually learn to speak: by thinking about what you want
                to say in real situations, then discovering how to say it naturally in Thai.
              </p>
            </section>

            {/* Smart Translations with Guru Explanations */}
            <section>
              <h2>Smart Translations with Guru Explanations</h2>
              <p>
                Every translation comes with a "Guru explanation"—your personal Thai language teacher
                breaking down exactly how the phrase works. See the context, understand grammar points,
                and get word-by-word breakdowns that help you build vocabulary quickly. This isn't
                just translation; it's comprehension.
              </p>
              <p>
                The romanization shows you how to pronounce Thai words using familiar letters, while
                the audio lets you hear the pronunciation. Together, these features help you bridge
                the gap between reading Thai script and speaking confidently.
              </p>
            </section>

            {/* Flashcard Creation & Spaced Repetition */}
            <section>
              <h2>Flashcard Creation & Spaced Repetition</h2>
              <p>
                Learning phrases is one thing—remembering them is another. That's why ThaiCopilot
                makes flashcard creation effortless. With one click, transform any phrase or individual
                word from your conversations into a flashcard. The front shows the English phrase; the
                back reveals the Thai translation, romanization, and audio.
              </p>
              <p>
                ThaiCopilot uses spaced repetition—the scientifically proven method for long-term
                memory retention. After each review, rate how easily you recalled the phrase. The
                system automatically schedules your next review at the optimal time: difficult phrases
                appear more frequently, while mastered phrases appear less often. This intelligent
                scheduling ensures you spend time on what you need most, not what you already know.
              </p>
            </section>

            {/* Your Personal Learning Workflow */}
            <section>
              <h2>Your Personal Learning Workflow</h2>
              <p>
                Here's the simple workflow that works: Use conversations to generate useful phrases
                based on your real-life situations. Create flashcards from those phrases. Practice
                daily with spaced repetition. As phrases move into long-term memory, speaking becomes
                automatic. You stop translating in your head and start responding naturally in Thai.
              </p>
              <p>
                The dashboard tracks your progress—conversations started, flashcards created, words
                mastered. More features are coming: progress tracking across vocabulary, AI-generated
                learning plans tailored to your background and goals, and language packs for specific
                scenarios. But the core remains simple: think, translate, practice, remember, speak.
              </p>
            </section>
          </div>

          {/* CTA Section */}
          <section className="not-prose mt-16">
            <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-12 text-center dark:border-indigo-900 dark:from-indigo-950/30 dark:to-purple-950/30">
              <h2 className="text-foreground mb-4 text-3xl font-bold">
                Ready to Start Learning Thai?
              </h2>
              <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
                Join early users who are building practical Thai skills through
                AI-powered conversations and smart flashcards.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Button asChild size="lg" className="px-8">
                  <Link href={signUpPath()}>Get Started Free</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="px-8">
                  <Link href="/about">Learn Our Story</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
